import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminUsersPage.css";
import { useI18n } from "../providers/I18nProvider";
import { canManageOrganization } from "../utils/roles";
import PaginationControls from "../components/PaginationControls";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../providers/ConfirmProvider";
import NoirAlert from "../components/NoirAlert";
import { TabBar, FilterBar, SearchInput, FilterField, EmptyState } from "../components/ListToolbar";

const emptyCreateForm = {
  name: "",
  surname: "",
  email: "",
  password: "",
  username: "",
  registry_number: "",
  date_of_birth: "",
  department: "",
  section: "",
  gender: "",
  role_id: 2, // primary rol; ek roller kullanici olusturulduktan sonra duzenlemeden eklenebilir
  team_id: "",
  manager_id: "",
  title: "",
  is_active: true,
  total_leave_quota_days: "",
  leave_base_date: "",
  leave_accrual_start_date: "",
};

const AdminUsersPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  const canManageUsers = canManageOrganization(me);

  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const [userTotalLeaveQuotaMin, setUserTotalLeaveQuotaMin] = useState(-14);

  useEffect(() => {
    apiClient
      .get("/site-settings")
      .then((res) => {
        const v = Number(res.data?.user_total_leave_quota_min);
        setUserTotalLeaveQuotaMin(Number.isFinite(v) ? v : -14);
      })
      .catch(() => {
        setUserTotalLeaveQuotaMin(-14);
      });
  }, []);

  const pageSizeKey = "pageSize.adminUsers";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  // Sekme + arama + filtreler
  const [tab, setTab] = useState("all"); // all | active | inactive
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [sortKey, setSortKey] = useState("name_asc");
  const [notification, setNotification] = useState({ message: "", type: "" });
  const notifyError = (err) =>
    setNotification({
      message:
        err?.response?.data?.msg ||
        (err?.response?.data ? JSON.stringify(err.response.data) : "") ||
        err?.message ||
        t("common.genericError"),
      type: "error",
    });

  const userRoleNames = (u) =>
    Array.isArray(u.role_names) && u.role_names.length > 0
      ? u.role_names
      : [u.role_name || String(u.role_id || "")].filter(Boolean);

  const tabCounts = useMemo(
    () => ({
      all: users.length,
      active: users.filter((u) => u.is_active).length,
      inactive: users.filter((u) => !u.is_active).length,
    }),
    [users]
  );

  const roleOptions = useMemo(
    () => Array.from(new Set(users.flatMap(userRoleNames))).sort((a, b) => a.localeCompare(b, "tr")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const list = users.filter((u) => {
      if (tab === "active" && !u.is_active) return false;
      if (tab === "inactive" && u.is_active) return false;
      if (roleFilter && !userRoleNames(u).includes(roleFilter)) return false;
      if (teamFilter === "__none" ? !!u.team_name : teamFilter && u.team_name !== teamFilter)
        return false;
      if (q) {
        const hay = `${u.name || ""} ${u.surname || ""} ${u.email || ""} ${u.username || ""} ${
          u.registry_number || ""
        } ${u.department || ""}`.toLocaleLowerCase("tr-TR");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const byText = (a, b) => String(a || "").localeCompare(String(b || ""), "tr");
    const sorters = {
      name_asc: (a, b) => byText(`${a.name} ${a.surname}`, `${b.name} ${b.surname}`),
      name_desc: (a, b) => byText(`${b.name} ${b.surname}`, `${a.name} ${a.surname}`),
      team_asc: (a, b) => byText(a.team_name, b.team_name),
      quota_desc: (a, b) => Number(b.total_leave_quota_days ?? -Infinity) - Number(a.total_leave_quota_days ?? -Infinity),
    };
    return [...list].sort(sorters[sortKey] || sorters.name_asc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, tab, search, roleFilter, teamFilter, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, roleFilter, teamFilter, sortKey]);

  useEffect(() => {
    const totalPages = Math.ceil((filteredUsers.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredUsers.length, page, pageSize]);

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const hasActiveFilter = Boolean(search || roleFilter || teamFilter);
  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setTeamFilter("");
  };

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [usersRes, rolesRes, teamsRes] = await Promise.all([
        apiClient.get("/users"),
        apiClient.get("/roles"),
        apiClient.get("/teams"),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setTeams(teamsRes.data);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name || "",
      surname: user.surname || "",
      email: user.email || "",
      username: user.username || "",
      registry_number: user.registry_number || "",
      date_of_birth: user.date_of_birth ? String(user.date_of_birth).slice(0, 10) : "",
      department: user.department || "",
      section: user.section || "",
      gender: user.gender || "",
      role_ids: Array.isArray(user.role_names)
        ? roles
            .filter((r) => user.role_names.includes(r.role_name))
            .map((r) => r.id)
        : user.role_id
        ? [user.role_id]
        : [],
      primary_role_id: user.role_id || null,
      team_id: user.team_id || "",
      manager_id: user.manager_id || "",
      title: user.title || "",
      is_active: user.is_active !== false,
      total_leave_quota_days:
        user.total_leave_quota_days !== undefined && user.total_leave_quota_days !== null
          ? user.total_leave_quota_days
          : "",
      leave_base_date: user.leave_base_date ? String(user.leave_base_date).slice(0, 10) : "",
      leave_accrual_start_date: user.leave_accrual_start_date ? String(user.leave_accrual_start_date).slice(0, 10) : "",
      password: "",
    });
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...createForm,
        role_id: Number(createForm.role_id),
        team_id: createForm.team_id ? Number(createForm.team_id) : null,
        manager_id: createForm.manager_id ? Number(createForm.manager_id) : null,
        total_leave_quota_days:
          createForm.total_leave_quota_days === "" || createForm.total_leave_quota_days === null
            ? null
            : Number(createForm.total_leave_quota_days),
      };
      await apiClient.post("/users", payload);
      setShowCreate(false);
      setCreateForm(emptyCreateForm);
      await fetchAll();
    } catch (err) {
      notifyError(err);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    try {
      const roleIds = Array.isArray(editForm.role_ids)
        ? editForm.role_ids.map(Number).filter((v) => v > 0)
        : [];
      if (roleIds.length === 0) {
        setNotification({ message: t("adminUsers.rolesRequired"), type: "error" });
        return;
      }
      const primaryId =
        editForm.primary_role_id && roleIds.includes(Number(editForm.primary_role_id))
          ? Number(editForm.primary_role_id)
          : roleIds[0];
      const payload = {
        ...editForm,
        role_id: primaryId, // legacy / primary
        role_ids: roleIds,
        primary_role_id: primaryId,
        team_id: editForm.team_id ? Number(editForm.team_id) : null,
        manager_id: editForm.manager_id ? Number(editForm.manager_id) : null,
        total_leave_quota_days:
          editForm.total_leave_quota_days === "" || editForm.total_leave_quota_days === null
            ? null
            : Number(editForm.total_leave_quota_days),
      };
      if (!payload.password) delete payload.password;
      await apiClient.put(`/users/${editUser.id}`, payload);
      setEditUser(null);
      setEditForm({});
      await fetchAll();
    } catch (err) {
      notifyError(err);
    }
  };

  const deleteUser = async (id) => {
    if (!(await confirm(t("admin.users.deleteConfirm")))) return;
    try {
      await apiClient.delete(`/users/${id}`);
      await fetchAll();
    } catch (err) {
      notifyError(err);
    }
  };

  if (loading) return <div className="admin-users">{t("common.loading")}</div>;
  if (error) return <div className="admin-users error">{t("common.error")}: {error}</div>;

  const downloadTemplate = async () => {
    try {
      const res = await apiClient.get("/users/template.xlsx", { responseType: "blob" });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "users-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(err);
    }
  };

  const downloadExport = async () => {
    try {
      const res = await apiClient.get("/users/export.xlsx", { responseType: "blob" });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `users-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(err);
    }
  };

  const runImport = async () => {
    if (!importFile) return;
    try {
      setImportResult(null);
      const form = new FormData();
      form.append("file", importFile);
      const res = await apiClient.post("/users/bulk-xlsx", form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setImportResult(res.data);
      setImportFile(null);
      await fetchAll();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="admin-users">
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      <div className="admin-users-header">
        <h2>{t("admin.users.title")}</h2>
        {canManageUsers && (
          <button className="primary" onClick={() => setShowCreate(true)}>
            {t("admin.users.newUser")}
          </button>
        )}
      </div>

      {canManageUsers && (
        <div className="import-card">
          <div className="import-head">
            <div>
              <h3>{t("admin.users.import.title")}</h3>
              <p className="muted">{t("admin.users.import.help")}</p>
              <p className="muted">{t("admin.users.import.passwordNote")}</p>
            </div>
            <button className="ghost" type="button" onClick={downloadTemplate}>
              {t("admin.users.import.downloadTemplate")}
            </button>
            <button className="ghost" type="button" onClick={downloadExport}>
              {t("admin.users.import.exportButton")}
            </button>
          </div>
          <div className="import-actions">
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
            <button className="primary" type="button" onClick={runImport} disabled={!importFile}>
              {t("admin.users.import.importButton")}
            </button>
          </div>

          {importResult && (
            <div className="import-result">
              <h4>{t("admin.users.import.resultTitle")}</h4>
              <div className="import-stats">
                <span>
                  {t("admin.users.import.created")}:{" "}
                  <strong>{importResult.created?.length || 0}</strong>
                </span>
                <span>
                  {t("admin.users.import.skipped")}:{" "}
                  <strong>{importResult.skipped?.length || 0}</strong>
                </span>
                <span>
                  {t("admin.users.import.failed")}:{" "}
                  <strong>{importResult.errors?.length || 0}</strong>
                </span>
                <span>
                  {t("admin.users.import.warnings")}:{" "}
                  <strong>{importResult.warnings?.length || 0}</strong>
                </span>
              </div>
              {(importResult.created || []).some((u) => u.password) && (
                <div className="import-passwords">
                  <p className="muted">
                    {t("admin.users.import.passwordNote")}
                  </p>
                  <ul>
                    {(importResult.created || [])
                      .filter((u) => u.password)
                      .map((u) => (
                        <li key={u.id} className="mono">
                          {u.username}: {u.password}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {(importResult.errors || []).length > 0 && (
                <div className="import-errors">
                  <ul>
                    {importResult.errors.map((e) => (
                      <li key={`${e.row}-${e.email || ""}`} className="mono">
                        row {e.row}: {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(importResult.skipped || []).length > 0 && (
                <div className="import-errors">
                  <ul>
                    {importResult.skipped.map((s) => (
                      <li key={`${s.row}-${s.email || s.username || ""}`} className="mono">
                        row {s.row}: {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(importResult.warnings || []).length > 0 && (
                <div className="import-errors">
                  <ul>
                    {importResult.warnings.map((w, idx) => (
                      <li key={`${w.code || "WARN"}-${idx}`} className="mono">
                        {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TabBar
        value={tab}
        onChange={setTab}
        ariaLabel={t("admin.users.title")}
        tabs={[
          { key: "all", label: t("adminUsers.tabs.all"), count: tabCounts.all },
          { key: "active", label: t("adminUsers.tabs.active"), count: tabCounts.active },
          { key: "inactive", label: t("adminUsers.tabs.inactive"), count: tabCounts.inactive },
        ]}
      />
      <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("adminUsers.filters.search")}
        />
        <select
          className="leaves-filter-input lt-input"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label={t("admin.users.fields.role")}
        >
          <option value="">{t("adminUsers.filters.allRoles")}</option>
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          className="leaves-filter-input lt-input"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          aria-label={t("admin.users.fields.team")}
        >
          <option value="">{t("adminUsers.filters.allTeams")}</option>
          <option value="__none">{t("adminUsers.filters.noTeam")}</option>
          {teams.map((tm) => (
            <option key={tm.id} value={tm.name}>
              {tm.name}
            </option>
          ))}
        </select>
        <FilterField label={t("adminUsers.filters.sort")}>
          <select
            className="lt-input"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          >
            <option value="name_asc">{t("adminUsers.filters.sortNameAsc")}</option>
            <option value="name_desc">{t("adminUsers.filters.sortNameDesc")}</option>
            <option value="team_asc">{t("adminUsers.filters.sortTeam")}</option>
            <option value="quota_desc">{t("adminUsers.filters.sortQuota")}</option>
          </select>
        </FilterField>
      </FilterBar>

      {filteredUsers.length === 0 && (
        <EmptyState
          message={t("adminUsers.emptyFiltered")}
          action={
            hasActiveFilter ? (
              <button type="button" className="ghost" onClick={resetFilters}>
                {t("common.resetFilters")}
              </button>
            ) : null
          }
        />
      )}

      <div className="table-wrap">
        <PaginationControls
          totalItems={filteredUsers.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        <table className="users-table">
          <thead>
            <tr>
              <th>{t("admin.users.fields.name")}</th>
              <th>{t("admin.users.fields.surname")}</th>
              <th>{t("admin.users.fields.email")}</th>
              <th>{t("admin.users.fields.username")}</th>
              <th>{t("admin.users.fields.role")}</th>
              <th>{t("admin.users.fields.team")}</th>
              <th>{t("admin.users.fields.manager")}</th>
              <th>{t("admin.users.fields.leaveBaseDate")}</th>
              <th>{t("admin.users.fields.leaveAccrualStartDate")}</th>
              <th>{t("admin.users.fields.nextAccrualEntitlementDays")}</th>
              <th>{t("admin.users.totalLeave")}</th>
              <th>{t("admin.users.active")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pagedUsers.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.surname}</td>
                <td className="mono">{u.email}</td>
                <td className="mono">{u.username}</td>
                <td className="mono">
                  {Array.isArray(u.role_names) && u.role_names.length > 0
                    ? u.role_names.join(", ")
                    : u.role_name || u.role_id}
                </td>
                <td>{u.team_name || "-"}</td>
                <td>
                  {u.manager_name
                    ? `${u.manager_name} ${u.manager_surname} (${u.manager_username})`
                    : "-"}
                </td>
                <td className="mono">{u.leave_base_date ? String(u.leave_base_date).slice(0, 10) : "-"}</td>
                <td className="mono">{u.leave_accrual_start_date ? String(u.leave_accrual_start_date).slice(0, 10) : "-"}</td>
                <td className="mono">{u.next_accrual_entitlement_days ?? "-"}</td>
                <td className="mono">
                  {u.total_leave_quota_days !== null && u.total_leave_quota_days !== undefined
                    ? u.total_leave_quota_days
                    : "-"}
                </td>
                <td>{u.is_active ? t("common.yes") : t("common.no")}</td>
                <td>
                  <div className="actions">
                    {canManageUsers ? (
                      <>
                        <button className="ghost" onClick={() => openEdit(u)}>
                          {t("common.edit")}
                        </button>
                        <button className="danger" onClick={() => deleteUser(u.id)}>
                          {t("common.delete")}
                        </button>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationControls
          totalItems={filteredUsers.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {showCreate && (
        <div className="modal-backdrop" onMouseDown={() => setShowCreate(false)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{t("admin.users.newUser")}</h3>
            <form className="form-grid" onSubmit={submitCreate}>
              <label>
                {t("admin.users.fields.name")}
                <input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.surname")}
                <input
                  value={createForm.surname}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, surname: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.email")}
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.password")}
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, password: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.username")}
                <input
                  value={createForm.username}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, username: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.registryNumber")}
                <input
                  value={createForm.registry_number}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, registry_number: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.birthDate")}
                <input
                  type="date"
                  value={createForm.date_of_birth}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, date_of_birth: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                {t("admin.users.fields.leaveBaseDate")}
                <input
                  type="date"
                  value={createForm.leave_base_date}
                  onChange={(e) => setCreateForm({ ...createForm, leave_base_date: e.target.value })}
                />
              </label>
              <label>
                {t("admin.users.fields.leaveAccrualStartDate")}
                <input
                  type="date"
                  value={createForm.leave_accrual_start_date}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, leave_accrual_start_date: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.role")}
                <select
                  value={createForm.role_id}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, role_id: e.target.value })
                  }
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.role_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("admin.users.fields.team")}
                <select
                  value={createForm.team_id}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, team_id: e.target.value })
                  }
                >
                  <option value="">{t("common.noneSelected")}</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("admin.users.fields.manager")}
                <select
                  value={createForm.manager_id}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, manager_id: e.target.value })
                  }
                >
                  <option value="">{t("common.noneSelected")}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} {u.surname} ({u.username})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("admin.users.fields.department")}
                <input
                  value={createForm.department}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, department: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.section")}
                <input
                  value={createForm.section}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, section: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.title")}
                <input
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                />
              </label>
              <label>
                {t("admin.users.totalLeaveDays")}
                <input
                  type="number"
                  min={userTotalLeaveQuotaMin}
                  step="0.5"
                  value={createForm.total_leave_quota_days || ""}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      total_leave_quota_days: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.gender")}
                <select
                  value={createForm.gender}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, gender: e.target.value })
                  }
                >
                  <option value="">{t("common.noneSelected")}</option>
                  <option value="E">E</option>
                  <option value="K">K</option>
                </select>
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={createForm.is_active}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, is_active: e.target.checked })
                  }
                />
                {t("admin.users.active")}
              </label>

              <div className="form-actions">
                <button type="button" className="ghost" onClick={() => setShowCreate(false)}>
                  {t("common.cancel")}
                </button>
                <button className="primary" type="submit">
                  {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editUser && (
        <div className="modal-backdrop" onMouseDown={() => setEditUser(null)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{t("admin.users.editTitle", { username: editUser.username })}</h3>
            <form className="form-grid" onSubmit={submitEdit}>
              <label>
                {t("admin.users.fields.name")}
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </label>
              <label>
                {t("admin.users.fields.surname")}
                <input
                  value={editForm.surname}
                  onChange={(e) =>
                    setEditForm({ ...editForm, surname: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.email")}
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </label>
              <label>
                {t("admin.users.fields.username")}
                <input
                  value={editForm.username}
                  onChange={(e) =>
                    setEditForm({ ...editForm, username: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.registryNumber")}
                <input
                  value={editForm.registry_number || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, registry_number: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.birthDate")}
                <input
                  type="date"
                  value={editForm.date_of_birth || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, date_of_birth: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.passwordOptional")}
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) =>
                    setEditForm({ ...editForm, password: e.target.value })
                  }
                />
              </label>
              <div className="admin-users-roles-block">
                <div className="admin-users-roles-title">
                  {t("admin.users.fields.roles") || "Roller"}
                </div>
                <div className="admin-users-roles-list">
                  {roles.map((r) => {
                    const checked = (editForm.role_ids || []).map(Number).includes(Number(r.id));
                    const isPrimary = Number(editForm.primary_role_id) === Number(r.id);
                    return (
                      <div key={r.id} className="admin-users-role-row">
                        <label className="admin-users-role-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const current = new Set(
                                (editForm.role_ids || []).map(Number)
                              );
                              if (e.target.checked) {
                                current.add(Number(r.id));
                              } else {
                                current.delete(Number(r.id));
                              }
                              const next = Array.from(current);
                              // Primary kaldirilmaya calisilirsa primary'yi ilk
                              // kalan role'e ata.
                              const nextPrimary =
                                !next.includes(Number(editForm.primary_role_id))
                                  ? next[0] || null
                                  : editForm.primary_role_id;
                              setEditForm({
                                ...editForm,
                                role_ids: next,
                                primary_role_id: nextPrimary,
                              });
                            }}
                          />
                          <span>{r.role_name}</span>
                        </label>
                        {checked && (
                          <label className="admin-users-role-primary">
                            <input
                              type="radio"
                              name="primary_role_id"
                              checked={isPrimary}
                              onChange={() =>
                                setEditForm({
                                  ...editForm,
                                  primary_role_id: Number(r.id),
                                })
                              }
                            />
                            <span>{isPrimary ? "Birincil" : "Birincil yap"}</span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
                <small className="admin-users-roles-hint">
                  Birden fazla rol seçilebilir. İzin akışı ve varsayılan menü
                  birincil role göre çalışır.
                </small>
              </div>
              <label>
                {t("admin.users.fields.team")}
                <select
                  value={editForm.team_id}
                  onChange={(e) => setEditForm({ ...editForm, team_id: e.target.value })}
                >
                  <option value="">{t("common.noneSelected")}</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("admin.users.fields.manager")}
                <select
                  value={editForm.manager_id}
                  onChange={(e) =>
                    setEditForm({ ...editForm, manager_id: e.target.value })
                  }
                >
                  <option value="">{t("common.noneSelected")}</option>
                  {users
                    .filter((u) => u.id !== editUser.id)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} {u.surname} ({u.username})
                      </option>
                    ))}
                </select>
              </label>
              <label>
                {t("admin.users.fields.department")}
                <input
                  value={editForm.department || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, department: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.section")}
                <input
                  value={editForm.section || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, section: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.fields.title")}
                <input
                  value={editForm.title || ""}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
              </label>
              <label>
                {t("admin.users.fields.gender")}
                <select
                  value={editForm.gender || ""}
                  onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                >
                  <option value="">{t("common.noneSelected")}</option>
                  <option value="E">E</option>
                  <option value="K">K</option>
                </select>
              </label>
              <label>
                {t("admin.users.fields.leaveBaseDate")}
                <input
                  type="date"
                  value={editForm.leave_base_date || ""}
                  onChange={(e) => setEditForm({ ...editForm, leave_base_date: e.target.value })}
                />
              </label>
              <label>
                {t("admin.users.fields.leaveAccrualStartDate")}
                <input
                  type="date"
                  value={editForm.leave_accrual_start_date || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, leave_accrual_start_date: e.target.value })
                  }
                />
              </label>
              <label>
                {t("admin.users.totalLeaveDays")}
                <input
                  type="number"
                  min={userTotalLeaveQuotaMin}
                  step="0.5"
                  value={editForm.total_leave_quota_days || ""}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      total_leave_quota_days: e.target.value,
                    })
                  }
                />
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={!!editForm.is_active}
                  onChange={(e) =>
                    setEditForm({ ...editForm, is_active: e.target.checked })
                  }
                />
                {t("admin.users.active")}
              </label>

              <div className="form-actions">
                <button type="button" className="ghost" onClick={() => setEditUser(null)}>
                  {t("common.cancel")}
                </button>
                <button className="primary" type="submit">
                  {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
